import {
  Container,
  Card,
  Image,
  Title,
  Text,
  Button,
  Flex,
  Space,
  FileInput,
  Notification,
  Alert,
  Textarea,
  Group,
  List,
  ThemeIcon,
  Progress,
} from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";
import { useLocation } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import JSZip from "jszip";
import axios from "axios";
import { withTrackingParams } from "./trackingParams";

import dataControlImage from "./StepsImage/chatgpt1.png";
import exportDataImage from "./StepsImage/chatgpt2.png";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function ChatGPTSteps() {
  const query = useQuery();
  // Clickworker params
  const clickworkerUser = query.get("user") || "";
  const clickworkerUserId = query.get("user_id") || "";
  const clickworkerTaskId = query.get("task_id") || "";
  const clickworkerJobId = query.get("job_id") || "";
  const email_param = query.get("email") || "";
  // Use clickworker user_id if present, otherwise fallback to email param
  const effectiveEmail = clickworkerUserId || email_param;
  const apiUrl = process.env.REACT_APP_API_URL_2;

  /**
   * State
   */
  const [zipFile, setZipFile] = useState(null); // raw .zip chosen by user
  const [entries, setEntries] = useState([]); // [{ name, blob }]
  const [ignoredCount, setIgnoredCount] = useState(0);

  const [extracting, setExtracting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0); // 0‑100 during upload loop
  const [uploadStatus, setUploadStatus] = useState('idle');
  const [processedCount, setProcessedCount] = useState(0);
  const [totalToUpload, setTotalToUpload] = useState(0);

  const [notification, setNotification] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState('idle'); // idle, pending, success, error
  const [successCode, setSuccessCode] = useState('');

  // Takeout verification state
  const [takeoutCheckLoading, setTakeoutCheckLoading] = useState(true);
  const [takeoutComplete, setTakeoutComplete] = useState(false);
  const [bothComplete, setBothComplete] = useState(false);
  const [finalSuccessCode, setFinalSuccessCode] = useState('');

  // Track step via new unified tracking API
  const trackStep = useCallback(async (stepName, status = "completed", metadata = {}) => {
    try {
      if (!effectiveEmail) return;

      await axios.post(`${apiUrl}/track/step`, {
        user_id: effectiveEmail,
        step_name: stepName,
        donation_type: "chatgpt",
        status: status,
        metadata: {
          clickworker_user: clickworkerUser,
          clickworker_user_id: clickworkerUserId,
          clickworker_task_id: clickworkerTaskId,
          clickworker_job_id: clickworkerJobId,
          ...metadata
        }
      });
      console.log(`Step tracked: ${stepName}`);
    } catch (err) {
      console.error("Failed to track step:", err);
    }
  }, [effectiveEmail, apiUrl, clickworkerUser, clickworkerUserId, clickworkerTaskId, clickworkerJobId]);

  // Check takeout status on page load
  useEffect(() => {
    const checkTakeoutStatus = async () => {
      if (!effectiveEmail) {
        setTakeoutCheckLoading(false);
        return;
      }

      try {
        // Check takeout status from our new tracker
        const response = await axios.get(`${apiUrl}/takeout/status/${effectiveEmail}`);
        const data = response.data;

        // Check if ALL three conditions are met: takeout_requested, oauth_completed, folder_scheduled
        if (data.takeout_requested && data.oauth_completed && data.folder_scheduled) {
          setTakeoutComplete(true);
        } else {
          console.log("Takeout not complete. Status:", {
            takeout_requested: data.takeout_requested,
            oauth_completed: data.oauth_completed,
            folder_scheduled: data.folder_scheduled
          });
        }
      } catch (err) {
        console.error("Failed to check takeout status:", err);
        // If 404, takeout tracking doesn't exist - block ChatGPT flow
        if (err.response?.status === 404) {
          console.log("Takeout tracking not found - blocking ChatGPT flow");
          setTakeoutComplete(false);
        }
      } finally {
        setTakeoutCheckLoading(false);
      }
    };

    checkTakeoutStatus();
  }, [effectiveEmail, apiUrl]);

  // Initialize Clickworker session on page load
  useEffect(() => {
    const initClickworkerSession = async () => {
      if (!clickworkerUserId) return;

      try {
        await axios.post(`${apiUrl}/clickworker/init`, {
          clickworker_user: clickworkerUser,
          clickworker_user_id: clickworkerUserId,
          task_id: clickworkerTaskId,
          job_id: clickworkerJobId,
          email: email_param,
        });
        console.log("Clickworker session initialized");

        // Track that user landed on ChatGPT steps page
        trackStep("chatgpt_steps_started");
      } catch (err) {
        console.error("Failed to init Clickworker session:", err);
      }
    };

    initClickworkerSession();
  }, [clickworkerUserId, clickworkerUser, clickworkerTaskId, clickworkerJobId, email_param, apiUrl, trackStep]);

  // Check combined donation status after ChatGPT verification
  const checkCombinedStatus = useCallback(async () => {
    if (!effectiveEmail) return;

    try {
      const response = await axios.get(`${apiUrl}/donation/status/${effectiveEmail}`);
      const data = response.data;

      setBothComplete(data.both_complete);
      if (data.both_complete && data.success_code) {
        setFinalSuccessCode(data.success_code);
      }
    } catch (err) {
      console.error("Failed to check combined status:", err);
    }
  }, [effectiveEmail, apiUrl]);

  /**
   * Instruction text the user will copy‑paste into ChatGPT
   */
  const instructionText = `based on your interactions with me, can you predict the following attributes about me? I know they might be hard to predict etc. just give me your best guess. I only want answers to each of the questions below. No other text: 
1. What Religion am I 
2. what is my gender 
3. what is my age range (<18, 18-24, 25-34, 35-44, 44-55, 55+) 
4. which political party do I support? 
5. which state and country I am currently residing in 
6. what would the income bracket of my family be? (Less than ₹3 lakh, ₹3 lakh – ₹6 lakh, ₹6 lakh – ₹12 lakh, ₹12 lakh – ₹18 lakh, More than ₹18 lakh) 
7. how often do i engage in physical activity per week? 
8. can you predict my Big Five Inventory (Openness to Experience, Conscientiousness, Extraversion, Agreeableness, Neuroticism. answer 1-5 for each of those. 1 – Strongly disagree 2 – Disagree 3 – Neutral 4 – Agree 5 – Strongly agree) 
9. can you predict my mental health status: Depression, anxiety, stress, well being for each of those, answer from 1-4 
1 – Did not apply to me at all 
2 – Applied to me to some degree 
3 – Applied to me a considerable degree 
4 – Applied to me very much.`;

  /****************************  ZIP → entries  ****************************/
  async function handleZipChange(selected) {
    setZipFile(selected);
    if (!selected) {
      setEntries([]);
      setTotalToUpload(0);
      setProcessedCount(0);
      setProgress(0);
      setUploadStatus('idle');
      return;
    }

    setExtracting(true);
    setNotification(null);

    try {
      const zip = await JSZip.loadAsync(selected);

      const allowed = [];
      let skipped = 0;
      const tasks = Object.values(zip.files).map(async (entry) => {
        if (entry.dir) return; // ignore folders
        const lower = entry.name.toLowerCase();
        if (lower.endsWith(".html") || lower.endsWith(".json")) {
          const blob = await entry.async("blob");
          allowed.push({ name: entry.name.split("/").pop(), blob });
        } else {
          skipped += 1;
        }
      });

      await Promise.all(tasks);
      setEntries(allowed);
      setIgnoredCount(skipped);
      setTotalToUpload(allowed.length);
      setProcessedCount(0);
      setProgress(0);
      setUploadStatus('idle');
    } catch (err) {
      console.error("Bad ZIP:", err);
      setNotification({ color: "red", message: "Could not read that ZIP (is it corrupted?)" });
      setEntries([]);
      setIgnoredCount(0);
      setTotalToUpload(0);
      setProcessedCount(0);
      setProgress(0);
      setUploadStatus('error');
    } finally {
      setExtracting(false);
    }
  }

  /****************************  Upload loop  ****************************/
  async function handleUpload() {
    if (!entries.length) return;
    setUploading(true);
    setProgress(0);
    setNotification(null);
    setUploadStatus('uploading');
    setTotalToUpload(entries.length);
    setProcessedCount(0);

    // Track upload started
    trackStep("upload_started", "pending", { total_files: entries.length });

    let success = 0;
    for (let i = 0; i < entries.length; i++) {
      const { name, blob } = entries[i];
      const filenameWithEmail = `${effectiveEmail}_${name}`.replace(/\s+/g, "_");
      const fileForForm = new File([blob], filenameWithEmail, { type: blob.type });

      const form = new FormData();
      form.append("file", fileForForm);
      form.append("email", effectiveEmail);

      try {
        await axios.post(`${apiUrl}/upload-chatgpt-data/`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        success += 1;
      } catch (err) {
        console.error(`Failed uploading ${name}:`, err);
      }

      setProgress(Math.round(((i + 1) / entries.length) * 100));
      setProcessedCount(i + 1);
    }

    setUploading(false);

    if (success === entries.length) {
      setProgress(100);
      setProcessedCount(success);
      setUploadStatus('success');
      setEntries([]);
      setZipFile(null);
      setNotification({ color: "green", message: "All files uploaded successfully!" });
      // Track upload success
      trackStep("upload", "completed", { files_uploaded: success });
    } else if (success === 0) {
      setUploadStatus('error');
      setProgress(0);
      setProcessedCount(0);
      setNotification({ color: "red", message: "Upload failed. Please try again later." });
      // Track upload failure
      trackStep("upload", "failure", { files_uploaded: 0 });
    } else {
      setUploadStatus('partial');
      setNotification({
        color: "yellow",
        message: `${success}/${entries.length} files uploaded. Please retry the rest.`,
      });
      // Track partial upload
      trackStep("upload", "partial", { files_uploaded: success, total_files: entries.length });
    }
  }

  /****************************  Verification  ****************************/
  async function handleVerify() {
    if (!effectiveEmail) return;
    setVerificationStatus('pending');
    setSuccessCode('');

    // Track verification started
    trackStep("verify_started", "pending");

    try {
      const response = await axios.get(`${apiUrl}/verify-chatgpt-data/`, {
        params: { user_id: effectiveEmail }
      });
      setVerificationStatus('success');
      setSuccessCode(response.data.success_code);

      // Track verification success
      trackStep("verify", "completed", {
        success_code: response.data.success_code,
        total_messages: response.data.total_messages,
        unique_message_ids: response.data.unique_message_ids,
      });

      // Complete Clickworker session
      if (clickworkerUserId) {
        try {
          await axios.post(`${apiUrl}/clickworker/complete/${clickworkerUserId}`, null, {
            params: { success_code: response.data.success_code }
          });
          console.log("Clickworker session completed");
        } catch (completeErr) {
          console.error("Failed to complete Clickworker session:", completeErr);
        }
      }

      // Track complete step
      trackStep("complete", "completed", { success_code: response.data.success_code });

      // Check combined donation status to see if BOTH are complete
      await checkCombinedStatus();

    } catch (err) {
      console.error("Verification failed:", err);
      setVerificationStatus('error');
      const errorDetail = err.response?.data?.detail || err.message;
      setNotification({ color: "red", message: `Verification failed: ${errorDetail}` });

      // Track verification failure
      trackStep("verify", "failure", { error: errorDetail });
    }
  }

  /****************************  Clipboard helper  ****************************/
  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(instructionText);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  }

  /****************************  RENDER  ****************************/
  const buttonLabel = (() => {
    if (uploadStatus === 'uploading') return 'Uploading…';
    if (uploadStatus === 'success') return 'Upload Successful';
    if (uploadStatus === 'partial' || uploadStatus === 'error') return 'Retry Upload';
    return 'Upload Now';
  })();

  // Show loading while checking takeout status
  if (takeoutCheckLoading) {
    return (
      <Container size="sm" py="xl">
        <Title order={1} align="center" mb="lg">
          Verifying Takeout Status...
        </Title>
        <Text align="center">Please wait while we verify your Google Takeout donation.</Text>
      </Container>
    );
  }

  // Show error if takeout not complete
  if (!takeoutComplete && effectiveEmail) {
    return (
      <Container size="sm" py="xl">
        <Title order={1} align="center" mb="lg">
          Complete Google Takeout First
        </Title>
        <Alert title="Takeout Donation Required" color="yellow" mb="md">
          You must complete the Google Takeout donation before proceeding with ChatGPT data donation.
        </Alert>
        <Text align="center" mb="md">
          Please go back and complete Step 1 (Google Takeout) first.
        </Text>
        <Button fullWidth onClick={() => window.location.href = withTrackingParams('/donate')}>
          Go to Donation Page
        </Button>
      </Container>
    );
  }

  return (
    <Container size="sm" py="xl">
      <Title order={1} align="center" mb="lg">
        Step 2: Export & Upload Your ChatGPT Data
      </Title>

      {takeoutComplete && (
        <Alert title="Step 1 Complete" color="green" mb="md">
          Google Takeout donation verified. Now complete ChatGPT data donation below.
        </Alert>
      )}

      {!effectiveEmail && (
        <Alert title="Email or User ID Missing" color="red" mb="md">
          Please provide your email or user_id in the URL query, e.g. {" "}
          <Text component="span" weight={700}>
            ?email=you@example.com
          </Text>
          {" or "}
          <Text component="span" weight={700}>
            ?user_id=clickworker123
          </Text>
        </Alert>
      )}

      <Flex direction="column" gap="xl">
        {/* Step 1 */}
        <Card withBorder shadow="sm">
          <Title order={3}>Step 1: Copy Instructions</Title>
          <Text mt="sm">
            Copy‑paste the text below into your ChatGPT window and wait for the
            answer.
          </Text>
          <Space h="md" />
          <Textarea
            value={instructionText}
            readOnly
            autosize
            minRows={10}
            sx={{ maxWidth: 600 }}
          />
          <Space h="sm" />
          <Group position="center">
            <Button onClick={handleCopy}>{copySuccess ? "Copied!" : "Copy to Clipboard"}</Button>
          </Group>
        </Card>

        {/* Step 2 */}
        <Card withBorder shadow="sm">
          <Title order={3}>Step 2: Open Data Controls</Title>
          <Text mt="sm">
            In the ChatGPT app, go to <Text component="span" weight={700}>Settings &gt; Data Controls</Text>.<br />
            This is where you manage your data export requests.
          </Text>
          <Space h="md" />
          <Image src={dataControlImage} alt="Data Controls" radius="md" mx="auto" width={300} />
        </Card>

        {/* Step 3 */}
        <Card withBorder shadow="sm">
          <Title order={3}>Step 3: Request Data Export</Title>
          <Text mt="sm">
            Under <Text component="span" weight={700}>Account</Text>, tap <Text component="span" weight={700}>Export Data</Text>. Follow
            the prompts and ensure you select <Text component="span" weight={700}>Chat History</Text> and {" "}
            <Text component="span" weight={700}>Usage Data</Text>.
          </Text>
          <Space h="md" />
          <Image src={exportDataImage} alt="Export Data" radius="md" mx="auto" width={300} mt={8} />
        </Card>

        {/* Step 4: ZIP selection & client‑side extraction */}
        <Card withBorder shadow="sm">
          <Title order={3}>Step 4: Client‑side Extraction</Title>
          <Text mt="sm">
            Select your ChatGPT export <Text component="span" weight={700}>ZIP</Text>. We extract it <strong>right in your browser</strong>. Only
            <Text component="span" weight={700}> .html</Text> & <Text component="span" weight={700}>.json</Text> files are kept—images, audio & video are ignored and{' '}
            <strong>never leave your device</strong>.
          </Text>
          <Space h="md" />



          <FileInput
            label="Select ZIP"
            placeholder="No file selected"
            accept=".zip"
            value={zipFile}
            onChange={handleZipChange}
            disabled={!effectiveEmail || extracting || uploading}
            sx={{ maxWidth: 400 }}
          />

          {extracting && <Text mt="sm">Extracting… please wait.</Text>}

          {!!entries.length && (
            <>
              <Space h="md" />
              <Text>
                <strong>{entries.length}</strong> eligible text files found. All media files (images, videos, audios, documents) are ignored:
              </Text>
              <List
                mt="sm"
                spacing="xs"
                icon={
                  <ThemeIcon color="teal" size={18} radius="xl">
                    <IconCheck size={12} />
                  </ThemeIcon>
                }
              >
                {entries.slice(0, 5).map((e) => (
                  <List.Item key={e.name}>{e.name}</List.Item>
                ))}
                {entries.length > 5 && <List.Item>…and {entries.length - 5} more</List.Item>}
              </List>
              {ignoredCount > 0 && (
                <Text size="sm" color="dimmed" mt="xs">
                  {ignoredCount} media file{ignoredCount > 1 && "s"} skipped.
                </Text>
              )}
            </>
          )}

          <Space h="md" />
          <Button
            onClick={handleUpload}
            loading={uploading}
            disabled={!entries.length || uploading}
            fullWidth
          >
            {buttonLabel}
          </Button>

          {uploadStatus !== 'idle' && totalToUpload > 0 && (
            <>
              <Space h="sm" />
              <Progress value={progress} striped={uploading} animated={uploading} />
              <Text align="center" mt="sm">
                {processedCount}/{totalToUpload} files uploaded ({progress}%)
              </Text>
            </>
          )}
          {uploadStatus === 'success' && (
            <>
              <Space h="md" />
              <Button onClick={handleVerify} loading={verificationStatus === 'pending'} fullWidth>
                {verificationStatus === 'pending' ? 'Verifying...' : 'Verify Data'}
              </Button>
              {verificationStatus === 'success' && successCode && (
                <>
                  <Alert title="ChatGPT Data Verified" color="teal" mt="md">
                    Your ChatGPT data has been verified successfully.
                  </Alert>

                  {bothComplete && finalSuccessCode ? (
                    <Alert title="Both Donations Complete!" color="green" mt="md">
                      <Text size="lg" weight={700} align="center">
                        Your Success Code: {finalSuccessCode}
                      </Text>
                      <Text size="sm" mt="sm" align="center">
                        Use this code to complete your submission.
                      </Text>
                    </Alert>
                  ) : (
                    <Alert title="Checking Combined Status..." color="blue" mt="md">
                      <Text>Verifying both donations are complete...</Text>
                    </Alert>
                  )}
                </>
              )}
            </>
          )}
        </Card>
      </Flex>

      {notification && (
        <Notification
          mt="lg"
          color={notification.color}
          onClose={() => setNotification(null)}
          title={notification.color === "green" ? "Success" : notification.color === "yellow" ? "Partial" : "Error"}
        >
          {notification.message}
        </Notification>
      )}

      <Text size="xs" color="dimmed" align="center" mt="xl">
        Note: All processing happens locally. Only the selected text files are
        sent to our server.
      </Text>
    </Container>
  );
}
